import { useEffect, useState } from 'react';
import { getLoadStats } from './loadTracker';

const MIN_VISIBLE_MS = 900; // floor so a fast cached load doesn't just flash
const QUIET_MS = 500; // no new fetch/image/video activity for this long => settled
const MAX_WAIT_MS = 15000; // absolute safety cap so a stray slow request can't hang forever
const TAU = 1500; // time constant for the "still working" asymptotic curve

/** Real, monotonically-increasing load progress (0-100) driven by actual
 * fetch + image/video/iframe activity tracked in loadTracker.ts — not a
 * fake demo timer. Only reaches 100 once nothing has started loading (or
 * finished loading) for QUIET_MS, meaning the page's data and media have
 * genuinely settled. */
export function useLoadProgress(): { progress: number; done: boolean } {
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      const now = performance.now();
      const elapsed = now - start;
      const stats = getLoadStats();
      const idle = stats.activeFetches === 0 && stats.activeMedia === 0;
      const quiet = now - stats.lastActivityAt >= QUIET_MS;
      const settled = idle && quiet && elapsed >= MIN_VISIBLE_MS;
      const timedOut = elapsed >= MAX_WAIT_MS;

      const asymptotic = 100 * (1 - Math.exp(-elapsed / TAU));
      const pct = settled || timedOut ? 100 : Math.min(92, asymptotic);

      setProgress((prev) => Math.max(prev, pct));

      if (pct >= 100) {
        setDone(true);
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, []);

  return { progress, done };
}
